import {
  Component,
  OnInit,
  Input,
  HostListener,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Chart, ChartConfiguration, ChartData } from "chart.js";
import { Subscription, fromEvent } from "rxjs";
import { filter } from "rxjs/operators";
import { EMAAC_ALERTS_PIE_CHART_CONFIGS } from "app/shared/chart-configs.shared";
import { IEmaacAlertStatistics } from "app/models/statistics.model";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { emaacAlertsCustomTooltipHandler } from "app/shared/chart-tooltips.shared";
import { StatisticsExportCaptureService } from "app/services/statistics-export-capture.service";
import {
  ALARM_CENTER_SIDEBAR_PDF_GROUP,
  StatisticsExportSnapshot,
  StatisticsExportWidget,
} from "app/services/statistics-export.models";
import { StatisticsPngDownloadBtnComponent } from "../statistics-png-download-btn/statistics-png-download-btn.component";

const EMAAC_PIE_EXPORT_SIZE_PX = 320;

@Component({
  selector: "emaac-alerts",
  templateUrl: "./emaac-alerts.component.html",
  styleUrls: ["./emaac-alerts.component.css"],
  standalone: true,
  imports: [TranslateModule, StatisticsPngDownloadBtnComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmaacAlertsComponent implements OnInit, OnDestroy, OnChanges, StatisticsExportWidget {
  constructor(
    private translate: TranslateService,
    private captureService: StatisticsExportCaptureService
  ) {}

  @Input() emaacAlertStats: IEmaacAlertStatistics;
  labels: string[];

  chart: Chart<"pie">;
  mouseRightClickSubscription: Subscription;

  @HostListener("contextmenu", ["$event"])
  onContextMenu(event: MouseEvent) {
    event.preventDefault();
  }

  resetZoom() {
    this.chart.resetZoom?.();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["emaacAlertStats"] && this.labels && this.emaacAlertStats) {
      this.refreshChart();
    }
  }

  refreshChart(): void {
    if (!this.labels || !this.emaacAlertStats) {
      return;
    }
    this.releaseChartCanvas();
    this.drawEmaacAlertsChart();
  }

  private releaseChartCanvas(): void {
    this.chart?.destroy();
    this.chart = undefined;
    const canvas = document.getElementById(
      "emaacAlertsChart"
    ) as HTMLCanvasElement | null;
    Chart.getChart(canvas ?? "emaacAlertsChart")?.destroy();
  }

  drawEmaacAlertsChart(): void {
    // Build a local config so the shared singleton is never mutated.
    const config = {
      ...EMAAC_ALERTS_PIE_CHART_CONFIGS,
      data: {
        labels: [...this.labels],
        datasets: [
          {
            data: [this.emaacAlertStats.emaAlerts, this.emaacAlertStats.finishedAlerts],
            backgroundColor: ["rgb(255, 118, 74)", "rgb(44, 63, 104)"],
          },
        ],
      },
      options: {
        ...EMAAC_ALERTS_PIE_CHART_CONFIGS.options,
        plugins: {
          ...EMAAC_ALERTS_PIE_CHART_CONFIGS.options?.plugins,
          tooltip: {
            ...EMAAC_ALERTS_PIE_CHART_CONFIGS.options?.plugins?.tooltip,
            external: emaacAlertsCustomTooltipHandler,
          },
        },
      },
    };

    this.chart = new Chart("emaacAlertsChart", config);
  }

  ngOnInit(): void {
    this.translate
      .get(["SENT_TO_EMA", "FINISHED_BY_ALARM_CENTER"])
      .subscribe((t) => {
        this.labels = [t.SENT_TO_EMA, t.FINISHED_BY_ALARM_CENTER];
        // Only refresh here when stats were bound before labels loaded; otherwise
        // ngOnChanges handles chart creation when emaacAlertStats arrives.
        if (this.emaacAlertStats && !this.chart) {
          this.refreshChart();
        }
      });

    // Init listening mouse right click event
    const mouseRightClick$ = fromEvent<MouseEvent>(document, "mousedown").pipe(
      filter((event: MouseEvent) => event.button === 2)
    );

    this.mouseRightClickSubscription = mouseRightClick$.subscribe(() => {
      this.resetZoom();
    });
  }

  async captureForExport(): Promise<StatisticsExportSnapshot | null> {
    if (!this.labels?.length || !this.emaacAlertStats) {
      return null;
    }

    // Build an off-screen config so the live pie chart is never mutated during capture.
    const exportConfig = {
      ...EMAAC_ALERTS_PIE_CHART_CONFIGS,
      data: {
        labels: [...this.labels],
        datasets: [
          {
            data: [this.emaacAlertStats.emaAlerts, this.emaacAlertStats.finishedAlerts],
            backgroundColor: ["rgb(255, 118, 74)", "rgb(44, 63, 104)"],
          },
        ],
      },
      options: { ...EMAAC_ALERTS_PIE_CHART_CONFIGS.options },
    };

    const pieChartSnapshot = await this.captureService.buildOffscreenChartSnapshot(
      exportConfig as ChartConfiguration,
      "emaac-alerts-pie",
      "ALERTS_WITH_FIRST_LETTER_UPPERCASE",
      {
        chartExportWidthPx: EMAAC_PIE_EXPORT_SIZE_PX,
        chartExportHeightPx: EMAAC_PIE_EXPORT_SIZE_PX,
        chartDevicePixelRatio: 2,
      }
    );
    if (!pieChartSnapshot?.imageDataUrl) {
      return null;
    }

    return {
      slug: "emaac-alerts",
      titleKey: "ALERTS_WITH_FIRST_LETTER_UPPERCASE",
      imageDataUrl: pieChartSnapshot.imageDataUrl,
      squareChartImageDataUrl: pieChartSnapshot.imageDataUrl,
      pdfPageGroup: ALARM_CENTER_SIDEBAR_PDF_GROUP,
      omitPdfTitle: true,
      pdfNativeLayout: "emaac-alerts-sidebar",
      emaacSidebarData: {
        totalAlerts: this.emaacAlertStats.totalAlerts,
        emaAlerts: this.emaacAlertStats.emaAlerts,
        finishedAlerts: this.emaacAlertStats.finishedAlerts,
        legendLabels: [this.labels[0], this.labels[1]],
      },
    };
  }

  ngOnDestroy(): void {
    this.mouseRightClickSubscription?.unsubscribe();
    this.releaseChartCanvas();
  }
}
